
import { defineComponent, ref } from 'vue';
import { mapActions, mapMutations, mapState } from 'vuex';
import { fromInteger2Float } from '@/libs';
import LineTitle from '@/components/LineTitle.vue';
import Button from '@/components/Button.vue';
import TokenLogo from '@/components/TokenLogo.vue';
import {
  approve,
  buyFixedPrice,
  buyWlFixedPrice,
  getSingerAddr,
} from '@/constants/contract';
import { freeMint, getTradeInfo, sendTradeInfo } from '@/api/dao';
import {
  delayTime,
  getBackFixedTokenAddress,
  getUTCTime,
  handleWalletError,
  formatDateLong,
  getOffsetTime,
} from '@/utils';
import { withThrottling, withWalletFlow } from '@/utils/with';
import { mittContract } from '@/libs/event';
import { Skeletor } from 'vue-skeletor';
import { notify } from '@kyvg/vue3-notification';
import Dialog from '@/components/Dialog.vue';
import {
  fixedPriceContract,
  fpWhitelistMintedNum,
  isFpWhitelist,
} from '@/constants/contract/read';
import dayjs from 'dayjs';

const getNetwork = (ins: any) => {
  return ins.dao.network;
};

export default defineComponent({
  name: 'ClaimFixedPrice',
  data: () => ({
    step: 0,
    sending: false,
    errorNotification: false,
    mintLoading: false,
    mintingDialog: false,
    mintedDialog: false,
  }),
  computed: {
    ...mapState({
      dao: (state: any) => state.dao.claimDAO,
      token: (state: any) => state.erc20.info,
      claimable: (state: any) => state.pass.gammaClaimable,
    }),
  },
  watch: {
    ['dao.tradeInfo'](val) {
      if (+this.dao.tradeInfo.paymentText == 0) {
        this.step = 2;
        return;
      }

      if (val.needApprove == undefined) return;
      if (+val.fixed_balance < +(val.payment || val.amount)) return;

      if (val.needApprove) {
        this.step = 1;
      } else {
        this.step = 2;
      }
    },
  },
  methods: {
    ...mapActions('dao', ['getClaimDAOClaimed', 'setClaimDAO']),
    ...mapActions('erc20', ['approve', 'isApproved', 'fetchTokenInfo']),
    ...mapActions('pass', ['stakeGamma', 'redeemGamma', 'claimableGamma']),
    ...mapMutations('erc20', ['resetClaimToken']),
    ...mapMutations('dao', ['subFixedBalance']),
    ...mapMutations(['openContractDialog', 'closeContractDialog']),
    formatDateLong,
    getOffsetTime,

    async canMint() {
      const contract = fixedPriceContract(
        this.dao.contractAddress,
        this.dao.network,
      );

      if (this.dao.tradeInfo.duration) {
        if (
          !dayjs(
            (await contract.mintDeadlineTime()).toString() * 1000 +
              new Date().getTimezoneOffset() * 60000,
          ).isAfter(getUTCTime())
        ) {
          notify({
            type: 'error',
            text: 'The Mint period has passed',
          });
          return false;
        }
      }

      const caller = await getSingerAddr();

      if (this.dao.tradeInfo.isWhiteList) {
        if (!(await contract.isWhite(caller))) {
          notify({
            type: 'error',
            text: 'Not in whitelist',
          });
          return false;
        }
      }

      if (
        this.dao.tradeInfo.mintLimit &&
        (await contract.mintNumber(caller)).toString() ==
          this.dao.tradeInfo.mintLimit
      ) {
        notify({
          type: 'error',
          text: 'Reach the Mint limit',
        });
        return false;
      }

      return true;
    },

    // TODO 这里需要判断 mint 次数 和 是否在白名单
    doApprove: withThrottling(
      withWalletFlow(async function (this: any) {
        try {
          mittContract(true, false);
          if (!(await this.canMint())) {
            mittContract(false);
            return;
          }
          await approve(
            getBackFixedTokenAddress(this.dao.tokenName, this.dao.chain),
            this.dao.contractAddress,
            async ({ transactionHash }: any) => {
              // await sendTradeInfo(this.dao.passType, this.dao.id, transactionHash);
              mittContract(false);
              this.dao.tradeInfo.needApprove = false;
              this.step = 2;
            },
          );
        } catch (error) {
          mittContract(false);
          handleWalletError(error);
        }
      }, getNetwork),
    ),
    doBuy: withThrottling(
      withWalletFlow(async function (this: any) {
        try {
          mittContract(true, false);
          if (!(await this.canMint())) {
            mittContract(false);
            return;
          }

          await buyFixedPrice(
            this.dao.contractAddress,
            this.dao.tokenName,
            this.dao.tradeInfo.payment,
            async ({ transactionHash }: any) => {
              try {
                await sendTradeInfo(
                  this.dao.passType,
                  this.dao.id,
                  transactionHash,
                  'BUY',
                );
              } catch (error) {
                /*  */
              }

              mittContract(false);
              await delayTime();
              await this.setClaimDAO({ name: this.dao.name });
            },
          );
        } catch (error) {
          mittContract(false);
          handleWalletError(error);
        }
      }, getNetwork),
    ),
    /* freeMint */
    doMint: withThrottling(
      withWalletFlow(async function (this: any) {
        try {
          this.mintLoading = true;
          const mintStatus = await freeMint(this.dao.id);
          if (mintStatus == 3) {
            throw new Error('Not in the whilelist.');
          }
          if (mintStatus == 1) {
            throw new Error("Yon've minted.");
          }
          this.mintingDialog = true;
          this.loopQuery();
        } catch (error: any) {
          /*  */
          notify({ type: 'error', text: error.message });
        }
        this.mintLoading = false;
      }, getNetwork),
    ),
    async loopQuery() {
      // const res = xxx;
      const res = await getTradeInfo(this.dao.passType, this.dao.id);

      if (res.mintStatus == 1) {
        await delayTime();
        await this.setClaimDAO({ name: this.dao.name });
        this.mintingDialog = false;
        this.mintedDialog = true;
      } else {
        await delayTime();
        this.loopQuery();
      }
    },
  },
  components: {
    Skeletor,
    LineTitle,
    Button,
    TokenLogo,
    Dialog,
  },
  setup() {
    const open = ref(false);
    return {
      fromInteger2Float,
      open,
    };
  },
});
